import { useOrderDetail, usePaymentResultPolling } from "@kupikupi/api";
import { formatPrice } from "@kupikupi/money";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TgPage } from "../components/TgPage";
import { useAuthToken } from "../lib/auth";
import { miniDisplayCurrency, miniDisplayLocale } from "../lib/locale-currency";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getParam(search: string, key: string): string {
  const u = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return u.get(key) ?? "";
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ResultRow({
  label,
  value,
  mono,
  accent,
  info,
}: {
  label: string;
  value: string;
  mono?: boolean;
  accent?: boolean;
  info?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "5px 0",
        fontSize: 12,
      }}
    >
      <span style={{ color: "var(--color-text-muted)" }}>{label}</span>
      <span
        style={{
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
          ...(mono ? { fontFamily: "monospace" } : {}),
          ...(accent ? { color: "var(--color-price)" } : {}),
          ...(info ? { color: "var(--color-secondary)" } : {}),
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function PayResultPage() {
  const { t, i18n } = useTranslation("pay");
  const displayLocale = miniDisplayLocale(i18n.language);
  const displayCurrency = miniDisplayCurrency(i18n.language);
  const authToken = useAuthToken();
  const navigate = useNavigate();

  // Read orderNo from URL search params (same pattern as catalog.tsx)
  const searchStr = useRouterState({ select: (s) => s.location.searchStr ?? "" });
  const orderNo = getParam(searchStr, "orderNo");
  const urlAmount = getParam(searchStr, "amount");

  // Elapsed-seconds counter (mirrors web pay-result-client.tsx)
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  // Format elapsed time (matches design: "已 18 秒" / "1:05")
  const m = Math.floor(seconds / 60);
  const ss = String(seconds % 60).padStart(2, "0");
  const elapsedDisplay =
    m > 0
      ? t("mini.polling_elapsed_long", { m, s: ss })
      : t("mini.polling_elapsed", { s: seconds });

  // Authoritative order detail — shows order no / amount
  const detailQ = useOrderDetail(authToken, orderNo);
  const detail = detailQ.data;

  // Polling hook (3s cadence, 5min timeout)
  const polling = usePaymentResultPolling(authToken, orderNo || null, !!orderNo);

  // Navigate to pay-success on terminal paid state
  useEffect(() => {
    if (polling.paid) {
      navigate({ to: "/pay-success" as never });
    }
  }, [polling.paid, navigate]);

  // Display values — authoritative order detail first, URL fallback, then "—"
  const PLACEHOLDER = "—";
  const displayOrderNo = orderNo || PLACEHOLDER;
  const displayAmount = detail
    ? formatPrice(detail.totalPayAmountRubKopecks, displayLocale, displayCurrency)
    : urlAmount || PLACEHOLDER;

  // State derivations
  const isFailed = polling.failed;
  const isTimedOut = polling.timedOut;
  const isTerminal = isFailed || isTimedOut;

  const displayStatus = isFailed
    ? `❌ ${t("mini.failed_title")}`
    : isTimedOut
      ? `⚠️ ${t("mini.timeout_title")}`
      : t("result.info_status_value");

  return (
    <TgPage tabbar={false}>
      {/*
       * No TgHeader — design spec: full-screen immersive page, no top nav bar.
       *
       * Gradient wrapper: TgPage doesn't accept style/className, so we put the
       * gradient (#EFEBFF → #D7CCFF) on a full-flex inner wrapper that covers
       * both the scrollable content area and the sticky CTA.
       */}
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          background: "linear-gradient(180deg,#EFEBFF,#D7CCFF)",
          minHeight: 0,
        }}
      >
        {/* Keyframe definitions */}
        <style>{`
          @keyframes kkPulse { 50% { box-shadow: 0 0 0 12px rgba(107,78,255,.15); } }
          @keyframes kkDot   { 50% { transform: scale(.6); } }
        `}</style>

        {/* Center-aligned content area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px 20px",
            textAlign: "center",
          }}
        >
          {/* Icon circle — purple info-soft + pulse animation (design spec) */}
          <div
            style={{
              width: 84,
              height: 84,
              borderRadius: "50%",
              background: isFailed
                ? "var(--color-warning-soft)"
                : "var(--color-info-soft)",
              display: "grid",
              placeItems: "center",
              fontSize: 36,
              marginBottom: 14,
              animation: isTerminal ? undefined : "kkPulse 1.6s infinite",
            }}
          >
            {isFailed ? "❌" : isTimedOut ? "⏳" : "🔄"}
          </div>

          {/* Title */}
          <h1
            style={{
              fontFamily: "-apple-system,system-ui,sans-serif",
              fontSize: 22,
              fontWeight: 900,
              margin: "0 0 8px",
            }}
          >
            {isFailed
              ? t("mini.failed_title")
              : isTimedOut
                ? t("mini.timeout_title")
                : t("mini.result_title")}
          </h1>

          {/* Subtitle */}
          <p
            style={{
              fontSize: 13,
              color: "var(--color-text-muted)",
              maxWidth: 280,
              lineHeight: 1.55,
              margin: "0 0 18px",
            }}
          >
            {isFailed
              ? t("mini.failed_hint")
              : isTimedOut
                ? t("mini.timeout_hint")
                : t("mini.result_hint")}
          </p>

          {/* Polling badge — only shown while actively polling */}
          {!isTerminal && (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: "10px 14px",
                display: "flex",
                gap: 8,
                alignItems: "center",
                fontSize: 11,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  background: "var(--color-secondary)",
                  borderRadius: "50%",
                  animation: "kkDot 1s infinite",
                  flexShrink: 0,
                }}
              />
              <span>
                {t("result.poll_label")}
                <strong style={{ fontFamily: "monospace" }}>{elapsedDisplay}</strong>
              </span>
            </div>
          )}

          {/* Order info card */}
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: "14px 18px",
              width: "100%",
              maxWidth: 300,
              textAlign: "left",
            }}
          >
            <ResultRow label={t("result.info_order_no")} value={displayOrderNo} mono />
            <ResultRow label={t("result.info_amount")} value={displayAmount} accent />
            <ResultRow
              label={t("result.info_status")}
              value={displayStatus}
              info={!isTerminal}
            />
          </div>

          {/* Idempotency note — design spec bottom text */}
          <p
            style={{
              fontSize: 10,
              color: "var(--color-text-muted)",
              maxWidth: 280,
              lineHeight: 1.5,
              marginTop: 18,
            }}
          >
            {t("mini.idempotency_note")}
          </p>
        </div>

        {/* Bottom CTA — full-width tg-main style (design spec: "回订单列表") */}
        <Link
          to={"/orders" as never}
          className="tg-main"
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {t("mini.orders_cta")}
        </Link>
      </div>
    </TgPage>
  );
}
